/**
 * Сообщения от игроков.
 *
 * До сих пор сообщить о проблеме было некуда: битый кадр или неверная точка
 * оставались известны только тому, кто на них наткнулся. Список отсортирован
 * от новых к старым — разбирать очередь удобнее с головы.
 */
import { List, ShowButton, useTable } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import { Space, Table, Tag, Typography } from "antd";

import { FEEDBACK_KINDS, FEEDBACK_STATUSES, kindLabel, statusTag } from "../../lib/feedback";
import { formatDateTime } from "../../lib/constants";

export interface FeedbackItem {
  id: string;
  author?: { id?: string; username?: string };
  kind?: string;
  topic?: string;
  message?: string;
  questId?: string;
  questTitle?: string;
  photoId?: string;
  status?: string;
  replies?: { id: string; message: string }[];
  createdAt?: string;
}

export function FeedbackList() {
  const { tableProps } = useTable<FeedbackItem, HttpError>({
    resource: "feedback",
    sorters: { mode: "off" },
  });

  return (
    <List title="Сообщения от игроков">
      <Table {...tableProps} rowKey="id">
        <Table.Column<FeedbackItem>
          title="Когда"
          dataIndex="createdAt"
          width={150}
          render={(value?: string) => formatDateTime(value)}
        />

        <Table.Column<FeedbackItem>
          title="Тип"
          dataIndex="kind"
          width={170}
          filters={FEEDBACK_KINDS.map((item) => ({ text: item.label, value: item.value }))}
          filterMultiple={false}
          render={(kind?: string, record?: FeedbackItem) => (
            <Space direction="vertical" size={0}>
              <Tag>{kindLabel(kind)}</Tag>
              {record?.topic && <Typography.Text type="secondary">{record.topic}</Typography.Text>}
            </Space>
          )}
        />

        <Table.Column<FeedbackItem>
          title="Сообщение"
          dataIndex="message"
          render={(message?: string, record?: FeedbackItem) => (
            <Space direction="vertical" size={2}>
              <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                {message}
              </Typography.Paragraph>
              {record?.questTitle && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Квест: {record.questTitle}
                </Typography.Text>
              )}
            </Space>
          )}
        />

        <Table.Column<FeedbackItem>
          title="От кого"
          width={160}
          render={(_, record) => record.author?.username ?? "—"}
        />

        <Table.Column<FeedbackItem>
          title="Статус"
          dataIndex="status"
          width={140}
          filters={FEEDBACK_STATUSES.map((item) => ({ text: item.label, value: item.value }))}
          filterMultiple={false}
          render={(status?: string) => statusTag(status)}
        />

        <Table.Column<FeedbackItem>
          title=""
          width={90}
          render={(_, record) => <ShowButton hideText size="small" recordItemId={record.id} />}
        />
      </Table>
    </List>
  );
}
